import React, { useRef, useEffect, useState } from 'react';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';

const LTPanelForm = () => {
    const dispatch = useDispatch();
    const { viewFormData } = useSelector((state) => state.checklist);
    const { formId, stationId } = useParams();
    const sigCanvas = useRef(null);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isEditable, setIsEditable] = useState(true);

    const { register, control, handleSubmit, setValue, getValues } = useForm({
        defaultValues: {
            station_id: stationId || '',
            project_name: '',
            equipment_manufacturer: '',
            serial_number: '',
            equipment_tag: '',
            rated_output: '',
            visual_checks: Array(13).fill({ status: '', remark: '' }),
            physical_checks: Array(7).fill({ status: '', remark: '' }),
            l1_comments: {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            },
            l1_status: 'draft',
            db_panel_healthy: '',
            functional_checks_status: '',
            testing_commissioning: '',
            insulation_resistance_test: '',
            functional_checks: Array(5).fill({ status: '', remark: '' }),
            test_instruments: [
                { description: 'IR Tester', serial_no: '', range: '', calibration_status: '' },
                { description: 'Digital Multi meter / Tong Tester', serial_no: '', range: '', calibration_status: '' },
            ],
            insulation_tests: Array(10).fill({ value: '', remarks: '' }),
            observations: { a: '', b: '', c: '' },
            l2_comments: {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            },
            l2_status: 'draft',
        },
    });

    const formValues = useWatch({ control });

    const autoSave = React.useCallback(
        _.debounce((data) => {
            const payload = {
                form_data: {
                    status: data.l1_status === 'draft' || data.l2_status === 'draft' ? 'draft' : 'pending',
                    form_id: formId,
                    formName: 'lt_panel',
                    station_id: data.station_id,
                    project_name: data.project_name,
                    equipment_manufacturer: data.equipment_manufacturer,
                    serial_number: data.serial_number,
                    equipment_tag: data.equipment_tag,
                    rated_output: data.rated_output,
                    l1: {
                        visual_checks: data.visual_checks,
                        physical_checks: data.physical_checks,
                        comments: data.l1_comments,
                        status: data.l1_status,
                    },
                    l2: {
                        db_panel_healthy: data.db_panel_healthy,
                        functional_checks_status: data.functional_checks_status,
                        testing_commissioning: data.testing_commissioning,
                        insulation_resistance_test: data.insulation_resistance_test,
                        functional_checks: data.functional_checks,
                        test_instruments: data.test_instruments,
                        insulation_tests: data.insulation_tests,
                        observations: data.observations,
                        comments: data.l2_comments,
                        status: data.l2_status,
                    },
                },
            };
            dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
        }, 2000),
        [dispatch, formId]
    );

    useEffect(() => {
        autoSave(formValues);
    }, [formValues]);

    useEffect(() => {
        if (formId) {
            dispatch({ type: VIEW_FORM, payload: { formId, formName: 'lt_panel' } });
        }
        setIsLoading(false);
    }, [dispatch, formId]);

    useEffect(() => {
        if (viewFormData) {
            const data = viewFormData || {};
            setIsEditable(!['pending', 'approved'].includes(data.status));
            setValue('station_id', data.station_id || stationId || '');
            setValue('project_name', data.project_name || '');
            setValue('equipment_manufacturer', data.equipment_manufacturer || '');
            setValue('serial_number', data.serial_number || '');
            setValue('equipment_tag', data.equipment_tag || '');
            setValue('rated_output', data.rated_output || '');
            setValue('visual_checks', Array.isArray(data.l1?.visual_checks)
                ? data.l1.visual_checks
                : Array(13).fill({ status: '', remark: '' }));
            setValue('physical_checks', Array.isArray(data.l1?.physical_checks)
                ? data.l1.physical_checks
                : Array(7).fill({ status: '', remark: '' }));
            setValue('l1_comments', data.l1?.comments || {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            });
            setValue('l1_status', data.l1?.status || 'draft');
            setValue('db_panel_healthy', data.l2?.db_panel_healthy || '');
            setValue('functional_checks_status', data.l2?.functional_checks_status || '');
            setValue('testing_commissioning', data.l2?.testing_commissioning || '');
            setValue('insulation_resistance_test', data.l2?.insulation_resistance_test || '');
            setValue('functional_checks', Array.isArray(data.l2?.functional_checks)
                ? data.l2.functional_checks
                : Array(5).fill({ status: '', remark: '' }));
            setValue('test_instruments', Array.isArray(data.l2?.test_instruments)
                ? data.l2.test_instruments
                : [
                    { description: 'IR Tester', serial_no: '', range: '', calibration_status: '' },
                    { description: 'Digital Multi meter / Tong Tester', serial_no: '', range: '', calibration_status: '' },
                ]);
            setValue('insulation_tests', Array.isArray(data.l2?.insulation_tests)
                ? data.l2.insulation_tests
                : Array(10).fill({ value: '', remarks: '' }));
            setValue('observations', data.l2?.observations || { a: '', b: '', c: '' });
            setValue('l2_comments', data.l2?.comments || {
                contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
                optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
            });
            setValue('l2_status', data.l2?.status || 'draft');
        }
    }, [viewFormData, stationId, setValue]);

    const handleImageUpload = (e, level, role) => {
        if (!isEditable) return;
        const file = e.target.files[0];
        const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB limit
        if (file) {
            if (file.size > maxSizeInBytes) {
                alert('File size exceeds 50kb. Please upload a smaller image.');
                handleResetSignature(level, role);
                sigCanvas.current?.clear();
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setValue(`${level}_comments.${role}.signature`, reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSignatureTypeChange = (level, role, type) => {
        if (!isEditable) return;
        setValue(`${level}_comments.${role}.signatureType`, type);
        setValue(`${level}_comments.${role}.signature`, '');
        if (type === 'digital') {
            setCurrentSignature({ level, role });
            setShowModal(true);
        }
    };

    const handleResetSignature = (level, role) => {
        if (!isEditable) return;
        setValue(`${level}_comments.${role}.signature`, '');
        setValue(`${level}_comments.${role}.signatureType`, 'upload');
    };

    const handleSaveSignature = () => {
        if (!isEditable) return;
        if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
            const signatureData = sigCanvas.current.toDataURL();
            setValue(`${currentSignature.level}_comments.${currentSignature.role}.signature`, signatureData);
        }
        setShowModal(false);
        sigCanvas.current?.clear();
    };

    const handleClearSignature = () => {
        if (!isEditable) return;
        sigCanvas.current?.clear();
    };

    const handleCloseModal = () => {
        if (!isEditable) {
            setShowModal(false);
            return;
        }
        handleResetSignature(currentSignature.level, currentSignature.role);
        setShowModal(false);
        sigCanvas.current?.clear();
    };

    const onSubmit = (level, status) => (data) => {
        if (!isEditable) {
            toast.info('Form is not editable in pending or approved status');
            return;
        }
        const l1_status = level === 'l1' ? status : data.l1_status;
        const l2_status = level === 'l2' ? status : data.l2_status;
        const common_status = l1_status === 'draft' || l2_status === 'draft' ? 'draft' : 'pending';

        const payload = {
            form_data: {
                status: common_status,
                form_id: formId,
                formName: 'lt_panel',
                station_id: data.station_id,
                project_name: data.project_name,
                equipment_manufacturer: data.equipment_manufacturer,
                serial_number: data.serial_number,
                equipment_tag: data.equipment_tag,
                rated_output: data.rated_output,
                l1: {
                    visual_checks: data.visual_checks,
                    physical_checks: data.physical_checks,
                    comments: data.l1_comments,
                    status: l1_status,
                },
                l2: {
                    db_panel_healthy: data.db_panel_healthy,
                    functional_checks_status: data.functional_checks_status,
                    testing_commissioning: data.testing_commissioning,
                    insulation_resistance_test: data.insulation_resistance_test,
                    functional_checks: data.functional_checks,
                    test_instruments: data.test_instruments,
                    insulation_tests: data.insulation_tests,
                    observations: data.observations,
                    comments: data.l2_comments,
                    status: l2_status,
                },
            },
        };
        dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
        toast.success(`Level ${level.toUpperCase()} ${status === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
        navigate(-1);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="page-body container-xl">
            <h2 className="page-title mb-4">LT Panel - Site Acceptance Testing</h2>
            {!isEditable && (
                <div className="alert alert-warning mb-4">
                    This form is in View Mode Only.
                </div>
            )}
            <Form>
                <h3>1. Equipment Details</h3>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Station ID</Form.Label>
                            <Form.Control type="text" {...register('station_id')} readOnly />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Project Name</Form.Label>
                            <Form.Control type="text" {...register('project_name')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Equipment Manufacturer</Form.Label>
                            <Form.Control type="text" {...register('equipment_manufacturer')} disabled={!isEditable} />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Serial Number</Form.Label>
                            <Form.Control type="text" {...register('serial_number')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Equipment Tag</Form.Label>
                            <Form.Control type="text" {...register('equipment_tag')} disabled={!isEditable} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Rated Output</Form.Label>
                            <Form.Control type="text" {...register('rated_output')} disabled={!isEditable} />
                        </Form.Group>
                    </Col>
                </Row>

                <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
                    <Tab eventKey="level1" title="Level 1 - Pre-Commissioning">
                        <h3 className="mt-4">2. Visual Checks</h3>
                        <Table bordered>
                            <thead>
                                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
                            </thead>
                            <tbody>
                                {[
                                    'Is the identification of equipment tags as per the SLD?',
                                    'Is the identification of the manufacturer’s nameplate as per the specification?',
                                    'Has the DB panel vendor TC check been completed?',
                                    'Are gaskets present on the panel doors?',
                                    'Is there a visual check of lamps and indicators?',
                                    'Is the panel properly double-earthed?',
                                    'Are there any exposed live parts, and are they covered?',
                                    'Is ventilation provided through louvers?',
                                    'Is there any physical damage, and are the door locking mechanisms intact?',
                                    'Is the power cable termination tight, with proper markings?',
                                    'Is the internal assembly free from damage and missing parts as per SLD?',
                                    'If any missing parts as per the SLD/GTP?',
                                    'Is the location and orientation as per the design and drawing?',
                                ].map((desc, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{desc}</td>
                                        <td>
                                            <Form.Check
                                                inline
                                                label="Yes"
                                                type="radio"
                                                name={`visual_checks_${index}`}
                                                value="Yes"
                                                {...register(`visual_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="No"
                                                type="radio"
                                                name={`visual_checks_${index}`}
                                                value="No"
                                                {...register(`visual_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="NA"
                                                type="radio"
                                                name={`visual_checks_${index}`}
                                                value="NA"
                                                {...register(`visual_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`visual_checks.${index}.remark`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <h3 className="mt-4">3. Physical Checks</h3>
                        <Table bordered>
                            <thead>
                                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
                            </thead>
                            <tbody>
                                {[
                                    'Is there any loose control wiring, and is the cable dressing proper?',
                                    'Are the internal components installed properly?',
                                    'Are all cable glands tightened securely?',
                                    'Is the panel board properly tightened on the base frame?',
                                    'Is the earth strip/cable properly tightened with the panel?',
                                    'Are the correct methods used for cable termination and tagging?',
                                    'Is the panel functioning correctly in both automatic and manual operation modes?',
                                ].map((desc, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{desc}</td>
                                        <td>
                                            <Form.Check
                                                inline
                                                label="Yes"
                                                type="radio"
                                                name={`physical_checks_${index}`}
                                                value="Yes"
                                                {...register(`physical_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="No"
                                                type="radio"
                                                name={`physical_checks_${index}`}
                                                value="No"
                                                {...register(`physical_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="NA"
                                                type="radio"
                                                name={`physical_checks_${index}`}
                                                value="NA"
                                                {...register(`physical_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`physical_checks.${index}.remark`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
                        <Table bordered>
                            <thead>
                                <tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr>
                            </thead>
                            <tbody>
                                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                                    <tr key={field}>
                                        <td>{field}</td>
                                        {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                                            <td key={role}>
                                                {field === 'Date' ? (
                                                    <Form.Control
                                                        type="date"
                                                        {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                                                        disabled={!isEditable}
                                                    />
                                                ) : field === 'Signature' ? (
                                                    <div>
                                                        <Form.Select
                                                            {...register(`l1_comments.${role}.signatureType`)}
                                                            onChange={(e) => handleSignatureTypeChange('l1', role, e.target.value)}
                                                            className="mb-2"
                                                            disabled={!isEditable}
                                                        >
                                                            <option value="upload">Image Upload</option>
                                                            <option value="digital">Digital Sign</option>
                                                        </Form.Select>
                                                        {getValues(`l1_comments.${role}.signatureType`) === 'upload' && (
                                                            <Form.Control
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={(e) => handleImageUpload(e, 'l1', role)}
                                                                className="mb-2"
                                                                disabled={!isEditable}
                                                            />
                                                        )}
                                                        {getValues(`l1_comments.${role}.signature`) && (
                                                            <div>
                                                                <img
                                                                    src={getValues(`l1_comments.${role}.signature`)}
                                                                    alt="Signature"
                                                                    style={{ maxWidth: '100px', marginTop: '5px' }}
                                                                />
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleResetSignature('l1', role)}
                                                                    className="mt-2"
                                                                    disabled={!isEditable}
                                                                >
                                                                    Reset Signature
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Form.Control
                                                        type="text"
                                                        {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                                                        disabled={!isEditable}
                                                    />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <Button
                            variant="success"
                            onClick={handleSubmit(onSubmit('l1', 'pending'))}
                            className="mt-4"
                            disabled={!isEditable}
                        >
                            Submit Level 1
                        </Button>
                        <Button
                            variant="secondary"
                            style={{ marginLeft: '10px' }}
                            onClick={handleSubmit(onSubmit('l1', 'draft'))}
                            className="mt-4"
                            disabled={!isEditable}
                        >
                            Save As Draft
                        </Button>
                    </Tab>

                    <Tab eventKey="level2" title="Level 2 - Commissioning">
                        <div className="alert alert-info mt-4">
                            Note: Ensure all below points are in co-ordinate with ANNEXURE: LT PANEL: COMMISSIONING PART: Level 2 and if all tests are found correct select Yes / No.
                        </div>

                        <h3 className="mt-4">5. Commissioning Part</h3>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>DB Panel Found Healthy & Ready to Power ON</Form.Label>
                                    <Form.Select {...register('db_panel_healthy')} disabled={!isEditable}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Functional Checks</Form.Label>
                                    <Form.Select {...register('functional_checks_status')} disabled={!isEditable}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Testing & Commissioning</Form.Label>
                                    <Form.Select {...register('testing_commissioning')} disabled={!isEditable}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Insulation Resistance Test</Form.Label>
                                    <Form.Select {...register('insulation_resistance_test')} disabled={!isEditable}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h3 className="mt-4">6. Functional Checks</h3>
                        <Table bordered>
                            <thead>
                                <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
                            </thead>
                            <tbody>
                                {[
                                    'Protection Check – Automatic & Manual',
                                    'Trial Tripping – MCCB, ELR, Contactor Tripping Test',
                                    'Annunciation – If any',
                                    'Alarms Trials – If any',
                                    'All pilot lamps glow check',
                                ].map((desc, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{desc}</td>
                                        <td>
                                            <Form.Check
                                                inline
                                                label="Yes"
                                                type="radio"
                                                name={`functional_checks_${index}`}
                                                value="Yes"
                                                {...register(`functional_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="No"
                                                type="radio"
                                                name={`functional_checks_${index}`}
                                                value="No"
                                                {...register(`functional_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                            <Form.Check
                                                inline
                                                label="NA"
                                                type="radio"
                                                name={`functional_checks_${index}`}
                                                value="NA"
                                                {...register(`functional_checks.${index}.status`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`functional_checks.${index}.remark`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <h3 className="mt-4">7. Test Instruments Details and Calibration Status</h3>
                        <Table bordered>
                            <thead>
                                <tr><th>Sr. No.</th><th>Instrument Description</th><th>Instrument Serial No.</th><th>Range</th><th>Calibration Status</th></tr>
                            </thead>
                            <tbody>
                                {[
                                    'IR Tester',
                                    'Digital Multi meter / Tong Tester',
                                ].map((desc, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{desc}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`test_instruments.${index}.serial_no`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`test_instruments.${index}.range`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`test_instruments.${index}.calibration_status`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <h3 className="mt-4">8. Testing & Commissioning (Insulation Resistance Test)</h3>
                        <h4>I) Insulation Resistance Test (Minimum value - 2 MΩ)</h4>
                        <div className="alert alert-info">
                            Note: During Megger testing isolate neutral link, remove Capacitor Bank, space heater fuses if any.
                        </div>
                        <Table bordered>
                            <thead>
                                <tr><th>Sr. No.</th><th>Phase to Phase</th><th>Value (MΩ/GΩ)</th><th>Remarks</th></tr>
                            </thead>
                            <tbody>
                                {[
                                    'R - Y',
                                    'Y - B',
                                    'B - R',
                                    'R - N',
                                    'Y - N',
                                    'B - N',
                                    'R - E',
                                    'Y - E',
                                    'B - E',
                                    'N - E',
                                ].map((phase, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{phase}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`insulation_tests.${index}.value`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                {...register(`insulation_tests.${index}.remarks`)}
                                                disabled={!isEditable}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <h3 className="mt-4">9. Observations during Commissioning / Testing (If Any)</h3>
                        {['a', 'b', 'c'].map((key) => (
                            <Form.Group className="mb-3" key={key}>
                                <Form.Label>{key.toUpperCase()}.</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...register(`observations.${key}`)}
                                    disabled={!isEditable}
                                />
                            </Form.Group>
                        ))}

                        <h3 className="mt-4">10. Level 2 Commissioning Comments</h3>
                        <Table bordered>
                            <thead>
                                <tr><th></th><th>Engineer - Contractor</th><th>Engineer - Charge Zone</th><th>Quality - Charge Zone</th><th>Optional</th></tr>
                            </thead>
                            <tbody>
                                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                                    <tr key={field}>
                                        <td>{field}</td>
                                        {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                                            <td key={role}>
                                                {field === 'Date' ? (
                                                    <Form.Control
                                                        type="date"
                                                        {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                                                        disabled={!isEditable}
                                                    />
                                                ) : field === 'Signature' ? (
                                                    <div>
                                                        <Form.Select
                                                            {...register(`l2_comments.${role}.signatureType`)}
                                                            onChange={(e) => handleSignatureTypeChange('l2', role, e.target.value)}
                                                            className="mb-2"
                                                            disabled={!isEditable}
                                                        >
                                                            <option value="upload">Image Upload</option>
                                                            <option value="digital">Digital Sign</option>
                                                        </Form.Select>
                                                        {getValues(`l2_comments.${role}.signatureType`) === 'upload' && (
                                                            <Form.Control
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={(e) => handleImageUpload(e, 'l2', role)}
                                                                className="mb-2"
                                                                disabled={!isEditable}
                                                            />
                                                        )}
                                                        {getValues(`l2_comments.${role}.signature`) && (
                                                            <div>
                                                                <img
                                                                    src={getValues(`l2_comments.${role}.signature`)}
                                                                    alt="Signature"
                                                                    style={{ maxWidth: '100px', marginTop: '5px' }}
                                                                />
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleResetSignature('l2', role)}
                                                                    className="mt-2"
                                                                    disabled={!isEditable}
                                                                >
                                                                    Reset Signature
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Form.Control
                                                        type="text"
                                                        {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                                                        disabled={!isEditable}
                                                    />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <Button
                            variant="success"
                            onClick={handleSubmit(onSubmit('l2', 'pending'))}
                            className="mt-4"
                            disabled={!isEditable}
                        >
                            Submit Level 2
                        </Button>
                        <Button
                            variant="secondary"
                            style={{ marginLeft: '10px' }}
                            onClick={handleSubmit(onSubmit('l2', 'draft'))}
                            className="mt-4"
                            disabled={!isEditable}
                        >
                            Save As Draft
                        </Button>
                    </Tab>
                </Tabs>

                <Modal show={showModal && isEditable} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Draw Signature</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <SignatureCanvas
                            ref={sigCanvas}
                            penColor="black"
                            canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
                        />
                        <div className="mt-2">
                            <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditable}>
                                Clear
                            </Button>
                            <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditable}>
                                Save Signature
                            </Button>
                        </div>
                    </Modal.Body>
                </Modal>
            </Form>
        </div>
    );
};

export default LTPanelForm;